import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ActionStatus, SetActionStatus } from "./bulk-action-bar";

interface ConfirmActionBarProps {
  status: ActionStatus;
  onStatusChange: SetActionStatus;
}

function useAnimatedStatus(status: ActionStatus, duration = 150) {
  const [displayed, setDisplayed] = useState(status);
  const [exiting, setExiting] = useState(false);
  const pendingRef = useRef<ActionStatus | null>(null);

  useEffect(() => {
    if (status.type === displayed.type) {
      setDisplayed(status);
      return;
    }
    pendingRef.current = status;
    setExiting(true);
    const t = setTimeout(() => {
      if (pendingRef.current) setDisplayed(pendingRef.current);
      setExiting(false);
      pendingRef.current = null;
    }, duration);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.type]);

  return { displayed, exiting };
}

export default function ConfirmActionBar({ status, onStatusChange }: ConfirmActionBarProps) {
  const [visible, setVisible] = useState(status.type !== "idle");
  const [barLeaving, setBarLeaving] = useState(false);
  const { displayed, exiting } = useAnimatedStatus(status);

  // Auto-dismiss: success rápido, error un poco más largo para poder leerlo.
  // Ambos se cierran solos, sin necesidad de un botón de cerrar.
  useEffect(() => {
    if (status.type !== "success" && status.type !== "error") return;
    const delay = status.type === "success" ? 1800 : 5000;
    const t = setTimeout(() => onStatusChange({ type: "idle" }), delay);
    return () => clearTimeout(t);
  }, [status.type, onStatusChange]);

  // Bar visibility
  useEffect(() => {
    if (status.type !== "idle") {
      setBarLeaving(false);
      setVisible(true);
    } else if (visible) {
      setBarLeaving(true);
      const t = setTimeout(() => {
        setVisible(false);
        setBarLeaving(false);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [status.type, visible]);

  if (!visible) return null;

  const contentClass = cn(
    "flex items-center gap-2 transition-all duration-150",
    exiting ? "opacity-0 scale-95" : "opacity-100 scale-100",
  );

  let content: React.ReactNode;

  if (displayed.type === "loading") {
    content = (
      <div className={cn(contentClass, "text-sm font-medium")}>
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <span>{displayed.message}</span>
      </div>
    );
  } else if (displayed.type === "success") {
    content = (
      <div className={cn(contentClass, "text-sm font-medium text-green-600 dark:text-green-400")}>
        <Check className="h-4 w-4 shrink-0" />
        <span>{displayed.message}</span>
      </div>
    );
  } else if (displayed.type === "error") {
    content = (
      <div className={cn(contentClass, "text-sm font-medium text-destructive")}>
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>{displayed.message}</span>
      </div>
    );
  } else if (displayed.type === "confirming") {
    content = (
      <div className={cn(contentClass)}>
        <span className="text-sm text-muted-foreground">{displayed.message}</span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={displayed.onCancel}>
            Cancelar
          </Button>
          <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={displayed.onConfirm}>
            Confirmar
          </Button>
        </div>
      </div>
    );
  } else {
    content = null;
  }

  const bar = (
    <div
      className="fixed left-1/2 z-[140] -translate-x-1/2 bottom-[calc(3.5rem+1.5rem)] md:bottom-6"
      style={{ pointerEvents: "none" }}
    >
      <div
        style={{ pointerEvents: "auto" }}
        className={cn(
          "flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2 shadow-lg transition-[width] duration-150",
          barLeaving
            ? "animate-out fade-out slide-out-to-bottom-3 duration-200"
            : "animate-in fade-in slide-in-from-bottom-3 duration-200",
        )}
      >
        {content}
      </div>
    </div>
  );

  return createPortal(bar, document.body);
}
